import { useEffect, useState, useCallback } from "react";
import { Contact, Download, MapPin, ArrowLeftRight } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import ListToolbar from "@/components/ListToolbar";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const TYPE_CLS = {
  buyer: "bg-blue-100 text-blue-700 border-blue-200",
  seller: "bg-amber-100 text-amber-800 border-amber-200",
  manufacturer: "bg-emerald-100 text-emerald-800 border-emerald-200",
};
const NEXT_TYPE = { buyer: "Seller", seller: "Manufacturer", manufacturer: "Buyer" };

export default function VisitorDirectory() {
  const [rows, setRows] = useState([]);
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("recent");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const p = new URLSearchParams();
    if (tab !== "all") p.set("type", tab);
    if (search) p.set("q", search);
    p.set("sort", sort);
    const { data } = await api.get(`/admin/visitors?${p.toString()}`);
    setRows(data);
  }, [tab, search, sort]);

  useEffect(() => { const t = setTimeout(load, 200); return () => clearTimeout(t); }, [load]);

  const flip = async (r) => {
    try {
      const { data } = await api.post(`/admin/visitors/${r.mobile}/flip-type`);
      toast.success(`${r.mobile} marked as ${data.type}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const res = await api.get("/admin/visitors/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "cleartile-visitors.csv";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Visitor list downloaded");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-5" data-testid="visitor-directory-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2">
            <Contact className="h-6 w-6 text-primary" /> Visitor Directory
          </h1>
          <p className="text-muted-foreground text-sm font-medium">Every buyer mobile captured before browsing, with visit counts and cities.</p>
        </div>
        <Button onClick={exportCsv} disabled={busy} className="gap-1.5 font-bold" data-testid="export-visitors-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="all" data-testid="visitor-tab-all">All</TabsTrigger>
          <TabsTrigger value="buyer" data-testid="visitor-tab-buyer">Buyers</TabsTrigger>
          <TabsTrigger value="seller" data-testid="visitor-tab-seller">Sellers</TabsTrigger>
          <TabsTrigger value="manufacturer" data-testid="visitor-tab-manufacturer">Manufacturers</TabsTrigger>
        </TabsList>
      </Tabs>

      <ListToolbar
        placeholder="Search mobile or city…"
        search={search} setSearch={setSearch}
        filters={[]} onFilter={() => {}}
        sort={sort} setSort={setSort}
        sorts={[{ value: "recent", label: "Recently active" }, { value: "visits", label: "Most visits" }, { value: "oldest", label: "Oldest" }]}
        onReset={() => { setSearch(""); setSort("recent"); }}
      />

      <div className="bg-card border rounded-xl overflow-x-auto ct-scroll">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Mobile</TableHead><TableHead>Type</TableHead><TableHead>Visits</TableHead><TableHead>Cities</TableHead>
            <TableHead>First Seen</TableHead><TableHead>Last Seen</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.mobile} data-testid={`visitor-row-${r.mobile}`}>
                <TableCell className="font-mono text-sm font-semibold">{r.mobile}</TableCell>
                <TableCell>
                  <Badge className={`border font-bold capitalize ${TYPE_CLS[r.type] || TYPE_CLS.buyer}`} data-testid={`visitor-type-${r.mobile}`}>{r.type}</Badge>
                </TableCell>
                <TableCell><Badge variant="outline" className="font-bold">{r.visits}</Badge></TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {(r.cities || []).length ? r.cities.map((c) => (
                      <span key={c} className="text-[11px] bg-secondary rounded-full px-2 py-0.5 font-semibold inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{c}</span>
                    )) : <span className="text-xs text-muted-foreground">—</span>}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{fmtDate(r.first_seen)}</TableCell>
                <TableCell className="text-xs">{fmtDate(r.last_seen)}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" data-testid={`flip-type-${r.mobile}`} onClick={() => flip(r)} className="gap-1.5 font-semibold">
                    <ArrowLeftRight className="h-3.5 w-3.5" /> Mark as {NEXT_TYPE[r.type] || "Seller"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-10 text-muted-foreground font-semibold">No visitors captured yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
